import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCMSStore } from '../stores/cmsStore';
import { useDialogStore } from '../stores/dialogStore';
import ProfileAvatar from './ProfileAvatar';

const Sidebar = ({ memberId }) => {
  const navigate = useNavigate();
  const { accessToken, profile, apparentRoleId } = useAuthStore();
  const { fetchMenu, getMenu } = useCMSStore();
  const { alert, confirm } = useDialogStore();
  const [menuItems, setMenuItems] = useState([]);
  const [memberProfile, setMemberProfile] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const location = useLocation();

  // Use provided memberId or fall back to current user's profile
  const currentMemberId = memberId || profile?.id;

  useEffect(() => {
    // Fetch profile sidebar menu for the current member
    const loadMenu = async () => {
      const result = await fetchMenu('PROFILE_SIDEBAR', accessToken, currentMemberId, apparentRoleId);
      if (result.success) {
        setMenuItems(result.items || []);
      }
    };

    // Fetch member profile info
    const loadMemberProfile = async () => {
      try {
        const response = await fetch(`/api/profile/details/${currentMemberId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setMemberProfile(data.profile);
        }
      } catch (err) {
        console.error('Failed to load member profile:', err);
      }
    };

    if (currentMemberId) {
      loadMenu();
      loadMemberProfile();
    }
  }, [accessToken, fetchMenu, currentMemberId, apparentRoleId]);

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) {
      alert('Error', 'Page title is required');
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch('/api/pages/personal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ title: newPageTitle })
      });

      const data = await response.json();

      if (data.success) {
        setShowCreateModal(false);
        setNewPageTitle('');
        await fetchMenu('PROFILE_SIDEBAR', accessToken, currentMemberId, apparentRoleId);
        alert('Success', 'Page created successfully');

        // Navigate to edit the new page
        navigate(`/profile/${currentMemberId}/page/edit/${data.page.id}`);
      } else {
        alert('Error', data.message || 'Failed to create page');
      }
    } catch (err) {
      console.error('Failed to create page:', err);
      alert('Error', 'Failed to create page');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeletePage = async (item) => {
    const confirmed = await confirm(
      'Delete Page',
      `Are you sure you want to delete "${item.title}"? This will permanently delete the page and all its content, including media files.`
    );

    if (!confirmed) return;

    setIsDeleting(item.id);
    try {
      const response = await fetch(`/api/pages/personal/${item.page_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        await fetchMenu('PROFILE_SIDEBAR', accessToken, currentMemberId, apparentRoleId);
        alert('Success', 'Page deleted successfully');
      } else {
        alert('Error', data.message || 'Failed to delete page');
      }
    } catch (err) {
      console.error('Failed to delete page:', err);
      alert('Error', 'Failed to delete page');
    } finally {
      setIsDeleting(null);
    }
  };

  const getMenuItemLink = (item) => {
    // All profile links use clean format: /profile/{profileId}/{page}
    if (!currentMemberId) return '#';

    if (item.page_slug) {
      // Custom user page
      return `/profile/${currentMemberId}/page/${item.page_slug}`;
    }
    if (item.route_code) {
      // System page (dashboard, profile, galleries, etc.)
      return `/profile/${currentMemberId}/${item.route_code}`;
    }
    if (item.url) {
      // External URL
      return item.url;
    }
    return '#';
  };

  const handleMenuClick = (e, item) => {
    if (item.url) {
      // External URL - let the browser handle it
      window.open(item.url, '_blank');
      e.preventDefault();
    }
  };

  const isActive = (item) => {
    const link = getMenuItemLink(item);
    return location.pathname === link;
  };

  return (
    <aside className="w-full md:w-64 surface shadow-sm rounded-lg p-4">
      {/* Profile Header */}
      <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <ProfileAvatar
          profileId={currentMemberId}
          displayName={memberProfile?.display_name || 'Member'}
          roleId={memberProfile?.role_id}
          size="lg"
          showName={true}
          showRole={true}
        />
      </div>

      {/* Navigation Menu */}
      <nav className="space-y-1">
        {menuItems.map((item) => {
          const isPersonalPage = item.menu_profile_id !== null;
          const isOwnProfile = currentMemberId === profile?.id;
          const showActions = isPersonalPage && isOwnProfile;

          return (
            <div key={item.id} className="group relative">
              <Link
                to={getMenuItemLink(item)}
                onClick={(e) => handleMenuClick(e, item)}
                className={`
                  flex items-center px-4 py-2 rounded-lg text-sm transition-colors
                  ${showActions ? 'pr-20' : ''}
                  ${
                    isActive(item)
                      ? 'bg-primary-600 !text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                  }
                `}
              >
                {item.icon && (
                  <span className="mr-3">
                    <i className={item.icon}></i>
                  </span>
                )}
                <span className="flex-1 truncate">{item.title}</span>
              </Link>

              {/* Draft/Published Badge + Action Buttons */}
              {showActions && item.page_id && (
                <div className="absolute right-2 top-2 flex items-center gap-1">
                  {/* Badge */}
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    item.has_published ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                  }`}>
                    {item.has_published ? 'Pub' : 'Draft'}
                  </span>

                  {/* Action Buttons */}
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => navigate(`/profile/${currentMemberId}/page/edit/${item.page_id}`)}
                      className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                      title="Edit page"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeletePage(item)}
                      disabled={isDeleting === item.id}
                      className="p-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs disabled:opacity-50"
                      title="Delete page"
                    >
                      {isDeleting === item.id ? (
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Add Page Button (only show when viewing own profile) */}
      {currentMemberId === profile?.id && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full flex items-center justify-center px-4 py-2 rounded-lg text-sm text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary transition-colors"
            title="Add custom page"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Page
          </button>
        </div>
      )}

      {/* Create Page Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="surface rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-primary mb-4">Create New Page</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreatePage()}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-primary"
                placeholder="Enter page title..."
                autoFocus
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPageTitle('');
                }}
                disabled={isCreating}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePage}
                disabled={isCreating || !newPageTitle.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isCreating && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isCreating ? 'Creating...' : 'Create Page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
